'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { en } from './en';
import type { PartialTranslation, TranslationKey } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Resolve a dot-notation key path from a (possibly partial) translation object. */
function resolve(obj: Record<string, unknown>, key: string): string | undefined {
    const parts = key.split('.');
    let current: unknown = obj;
    for (const part of parts) {
        if (current == null || typeof current !== 'object') return undefined;
        current = (current as Record<string, unknown>)[part];
    }
    return typeof current === 'string' ? current : undefined;
}

/** Replace {variable} placeholders with provided values. */
function interpolate(str: string, vars?: Record<string, string | number>): string {
    if (!vars) return str;
    return str.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

// Runtime cache for fetched language files — persists across re-renders.
const langCache = new Map<string, PartialTranslation>();

// ─── Context ──────────────────────────────────────────────────────────────────

interface I18nContextValue {
    /** Translate a key. Supports {variable} interpolation. Falls back to EN if key is missing. */
    t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
    /** Active language code, e.g. 'en', 'fr'. */
    lang: string;
    /** Change the active language. Persists to localStorage. Fetches file if needed. */
    setLang: (lang: string) => void;
    /** True while a non-EN language file is being fetched. */
    isLoading: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function I18nProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState('en');
    const [translations, setTranslations] = useState<PartialTranslation | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Hydrate from localStorage on mount (client-only).
    useEffect(() => {
        const stored = typeof window !== 'undefined' ? localStorage.getItem('nolia-lang') : null;
        if (stored && stored !== 'en') {
            setLangState(stored);
        }
    }, []);

    // Fetch non-EN language files on demand.
    useEffect(() => {
        if (lang === 'en') {
            setTranslations(null);
            return;
        }

        // Serve from cache if available.
        if (langCache.has(lang)) {
            setTranslations(langCache.get(lang)!);
            return;
        }

        setIsLoading(true);
        fetch(`/locales/${lang}.json`)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json() as Promise<PartialTranslation>;
            })
            .then((data) => {
                langCache.set(lang, data);
                setTranslations(data);
            })
            .catch((err) => {
                console.warn(`[i18n] Failed to load "${lang}":`, err);
                // Fall back to English silently.
                setLangState('en');
                if (typeof window !== 'undefined') localStorage.setItem('nolia-lang', 'en');
            })
            .finally(() => setIsLoading(false));
    }, [lang]);

    const setLang = useCallback((newLang: string) => {
        setLangState(newLang);
        if (typeof window !== 'undefined') {
            localStorage.setItem('nolia-lang', newLang);
        }
    }, []);

    const t = useCallback(
        (key: TranslationKey, vars?: Record<string, string | number>): string => {
            // Try active language translation first.
            if (translations && lang !== 'en') {
                const found = resolve(translations as Record<string, unknown>, key);
                if (found !== undefined) return interpolate(found, vars);
            }
            // Fall back to bundled EN.
            const fallback = resolve(en as unknown as Record<string, unknown>, key);
            return interpolate(fallback ?? key, vars);
        },
        [lang, translations],
    );

    const value = useMemo<I18nContextValue>(
        () => ({ t, lang, setLang, isLoading }),
        [t, lang, setLang, isLoading],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useI18n(): I18nContextValue {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
    return ctx;
}
